import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowLeft, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import LanguageSelector from '../components/LanguageSelector';
import { HreflangTags, useSyncLanguageFromURL } from '../components/HreflangTags';
import SeoMeta from '../components/SeoMeta';
import PublicPageLayout from '../components/layout/PublicPageLayout';
import { buildBreadcrumbJsonLd } from '../data/seoContent';
import { ObfuscatedEmail } from '../components/ObfuscatedEmail';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Localised SEO meta for the contact page
const CONTACT_SEO = {
  fr: {
    title: 'Contact — Drone Maps',
    description: "Contactez Drone Maps : support, partenariat, presse. Formulaire en ligne, réponse sous 48h.",
    keywords: 'contact Drone Maps, support drone, partenariat drone Europe, presse drone',
  },
  en: {
    title: 'Contact — Drone Maps',
    description: 'Contact Drone Maps: support, partnership, press. Online form, reply within 48h.',
    keywords: 'Drone Maps contact, drone support, drone partnership Europe, drone press',
  },
  de: {
    title: 'Kontakt — Drone Maps',
    description: 'Kontaktieren Sie Drone Maps: Support, Partnerschaft, Presse. Online-Formular, Antwort innerhalb 48 Std.',
    keywords: 'Drone Maps Kontakt, Drohnen-Support, Partnerschaft, Presse',
  },
  it: {
    title: 'Contatti — Drone Maps',
    description: 'Contatta Drone Maps: supporto, partnership, stampa. Modulo online, risposta entro 48 ore.',
    keywords: 'contatto Drone Maps, supporto drone, partnership drone, stampa',
  },
  es: {
    title: 'Contacto — Drone Maps',
    description: 'Contacta con Drone Maps: soporte, colaboración, prensa. Formulario online, respuesta en 48 h.',
    keywords: 'contacto Drone Maps, soporte dron, colaboración, prensa',
  },
};

export default function ContactPage() {
  useSyncLanguageFromURL();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const seo = CONTACT_SEO[lang] || CONTACT_SEO.fr;

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', company: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error | rate-limit
  const [errorMsg, setErrorMsg] = useState('');

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '', company: '' });
    } catch (err) {
      if (err.response?.status === 429) {
        setStatus('rate-limit');
        setErrorMsg(t('contact.rateLimit'));
      } else {
        setStatus('error');
        setErrorMsg(t('contact.error'));
      }
    }
  };

  const publicHeader = (
    <header className="bg-[#1b2236] text-white border-b border-slate-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="contact-header-logo">
            <img src="/logo-landscape.png" alt="Drone Maps" className="h-12 sm:h-14 w-auto" />
          </Link>
          <nav className="flex items-center gap-4">
            <Link to="/" className="text-slate-300 hover:text-white transition-colors flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              {t('common.back', 'Back')}
            </Link>
            <LanguageSelector variant="compact" />
            <Link to="/login" className="bg-sky-500 hover:bg-sky-600 text-white px-4 py-2 rounded-lg transition-colors">
              {t('nav.login', 'Login')}
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );

  return (
    <>
      <SeoMeta
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        rawTitle
        jsonLd={buildBreadcrumbJsonLd([
          { name: 'Drone Maps', url: 'https://drone-maps.eu' },
          { name: t('contact.title'), url: 'https://drone-maps.eu/contact' },
        ])}
      />
      <HreflangTags />

      <PublicPageLayout publicHeader={publicHeader} title={t('contact.title')}>
        <div className="min-h-screen bg-slate-50">
          {/* Hero */}
          <section className="bg-[#1b2236] text-white py-16">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <Mail className="w-12 h-12 text-sky-400 mx-auto mb-4" />
              <h1 className="text-4xl sm:text-5xl font-bold font-[Barlow] mb-4" data-testid="contact-h1">
                {t('contact.title')}
              </h1>
              <p className="text-lg text-slate-300">{t('contact.subtitle')}</p>
            </div>
          </section>

          {/* Form */}
          <section className="py-12">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
              <form
                onSubmit={onSubmit}
                className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 shadow-sm space-y-5"
                data-testid="contact-form"
              >
                {/* Honeypot - hidden from humans, visible to bots */}
                <div className="hidden" aria-hidden="true">
                  <label>Company (leave blank)</label>
                  <input
                    type="text"
                    name="company"
                    tabIndex="-1"
                    autoComplete="off"
                    value={form.company}
                    onChange={onChange}
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">{t('contact.name')} *</Label>
                    <Input
                      id="name"
                      name="name"
                      required
                      maxLength={120}
                      value={form.name}
                      onChange={onChange}
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">{t('contact.email')} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">{t('contact.subject')}</Label>
                  <Input
                    id="subject"
                    name="subject"
                    maxLength={200}
                    placeholder={t('contact.subjectPlaceholder')}
                    value={form.subject}
                    onChange={onChange}
                    data-testid="contact-subject"
                  />
                </div>

                <div>
                  <Label htmlFor="message">{t('contact.message')} *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    minLength={10}
                    maxLength={5000}
                    rows={6}
                    placeholder={t('contact.messagePlaceholder')}
                    value={form.message}
                    onChange={onChange}
                    data-testid="contact-message"
                  />
                  <p className="text-xs text-slate-400 mt-1">{form.message.length}/5000</p>
                </div>

                {status === 'success' && (
                  <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg text-sm" data-testid="contact-success">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>{t('contact.success')}</span>
                  </div>
                )}
                {(status === 'error' || status === 'rate-limit') && (
                  <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 text-rose-800 rounded-lg text-sm" data-testid="contact-error">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>{errorMsg}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full bg-sky-500 hover:bg-sky-600 text-white font-semibold"
                  data-testid="contact-submit"
                >
                  {status === 'loading' ? (
                    t('contact.sending')
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      {t('contact.send')}
                    </>
                  )}
                </Button>

                <p className="text-center text-sm text-slate-500 pt-2 border-t border-slate-100">
                  {t('contact.directEmail')}{' '}
                  <ObfuscatedEmail linkClassName="text-sky-600 hover:underline font-medium" />
                </p>
              </form>
            </div>
          </section>

          {/* Footer */}
          <footer className="bg-[#1b2236] text-slate-400 py-8 border-t border-slate-700">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm">
              © {new Date().getFullYear()} Drone Maps
            </div>
          </footer>
        </div>
      </PublicPageLayout>
    </>
  );
}
